import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import OSS from "ali-oss";
import { useEffect, useRef, useState } from "react";
import type { FunctionComponent } from "react";
import type { ControllerRenderProps, FieldErrors } from "react-hook-form";

import { config } from "shared/data";

import { usePostiesFormDrawerContext } from "scmp-app/components/posties/form-drawer/contexts";
import { StyledTextField } from "scmp-app/components/posties/student-form/styles";

import { UploadField } from "./styles";

type FileName = {
  originalName: string;
  uploadFileName: null | string;
};

type Props = {
  clearErrors: (name: string) => void;
  errors: FieldErrors;
  field: ControllerRenderProps;
  name: string;
  placeholder: null | string;
  required: boolean;
  setError: (name: string, error: { message: string }) => void;
  setValue: (name: string, value: string) => void;
};
export const FieldFileUpload: FunctionComponent<Props> = ({
  clearErrors,
  errors,
  field,
  name,
  placeholder,
  required,
  setError,
  setValue,
}) => {
  const ossClient = useRef<null | OSS>(null);
  const { ossCredential } = usePostiesFormDrawerContext();
  const [fileName, setFileName] = useState<FileName>({
    originalName: "",
    uploadFileName: null,
  });

  useEffect(() => {
    const { accessKeyId, accessKeySecret, stsToken } = ossCredential ?? {};
    if (!accessKeyId || !accessKeySecret || !stsToken) return;
    ossClient.current = new OSS({
      accessKeyId,
      accessKeySecret,
      bucket: config.sts.studentService.ossBucket,
      region: config.sts.studentService.ossRegion,
      stsToken,
    });
  }, [ossCredential]);

  const uploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ossClient.current) {
      throw new Error("OSS client not initialized");
    }

    try {
      if (file.size > 10 * 1024 * 1024) {
        setError(name, { message: "File too large" });
        setValue(name, "");
        return;
      }

      if (!file.type.startsWith("image/")) {
        setError(name, { message: "Invalid file type" });
        setValue(name, "");
        return;
      }

      const uploadFileName = `tmp/${file.name}`;
      await ossClient.current.put(uploadFileName, file);
      setValue(name, uploadFileName);
      clearErrors(name);
      setFileName({ originalName: file.name, uploadFileName });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to upload";
      setError(name, { message: errorMessage });
      setValue(name, "");
    }
  };

  const handleCancel = () => {
    setValue(name, "");
    setFileName({ originalName: "", uploadFileName: null });
    clearErrors(name);
  };

  return (
    <>
      {fileName.uploadFileName ? (
        <div>
          <StyledTextField
            {...field}
            disabled
            error={Boolean(errors[name])}
            fullWidth
            helperText={String(errors[name]?.message ?? "")}
            placeholder={placeholder ?? ""}
            required={required}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCancel} sx={{ p: 0 }}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            value={fileName.originalName}
            variant="outlined"
          />
          <input type="hidden" {...field} accept="image/*" />
        </div>
      ) : (
        <UploadField
          error={Boolean(errors[name])}
          fullWidth
          helperText={String(errors[name]?.message ?? "")}
          onChange={uploadFile}
          placeholder={placeholder ?? ""}
          required={required}
          slotProps={{
            htmlInput: {
              accept: "image/*",
            },
          }}
          type="file"
          variant="outlined"
        />
      )}
    </>
  );
};

FieldFileUpload.displayName = "FieldFileUpload";
